import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { RootState } from '../store/store';
import { UserPermission } from '../types/admin/accessManagement.types';
import {
  selectRpnConfidentialMapFromMinimalHierarchy,
  selectProductHierarchyMinimalLoading,
} from '../store/selectors/productHierarchySelectors';
import * as permissionsUtils from '../utils/permissionsUtils';

/**
 * Hook return type with permission checking functions
 */
export interface UsePermissionsReturn {
  // User permission data
  userPermission: UserPermission | null;
  isLoading: boolean;
  error: string | null;

  // User type checks
  isTechAdmin: boolean;
  isVerityAdmin: boolean;
  isReadOnly: boolean;
  hasExtendedAccessPL: boolean;

  // Permission check functions
  hasProductLineAccess: (productLineId: number) => boolean;
  isProductLineAdmin: (productLineId: number) => boolean;
  hasConfidentialAccessForProductLine: (productLineId: number) => boolean;
  canAccessConfidentialRPN: (productLineId: number, rpnId: number) => boolean;
  canEditProductLine: (productLineId: number) => boolean;
  canEditRPN: (productLineId: number, rpnId: number) => boolean;

  // Admin checks
  canManageAccessManagement: boolean;
  canAccessAdminSection: boolean;

  // Utility functions
  getAccessibleProductLineIds: () => number[];
  getAdminProductLineIds: () => number[];
  getConfidentialAccessProductLineIds: () => number[];
  hasAnyPermissions: boolean;

  // Confidential data map (for RPN confidential status lookup)
  rpnConfidentialMap: Record<number, boolean>;
  isConfidentialDataLoading: boolean;
}

/**
 * Custom hook for accessing user permissions and permission checking
 *
 * This hook provides:
 * - Current user's permission data from Redux
 * - Confidential RPN data map
 * - Permission checking functions
 * - Loading and error states
 *
 * @example
 * ```tsx
 * const {
 *   isTechAdmin,
 *   canEditRPN,
 *   hasProductLineAccess
 * } = usePermissions();
 *
 * if (isTechAdmin) {
 *   // Show admin features
 * }
 *
 * if (canEditRPN(productLineId, rpnId)) {
 *   // Enable edit button
 * }
 * ```
 */
export const usePermissions = (): UsePermissionsReturn => {
  // Get current user permissions from Redux
  const userPermission = useSelector((state: RootState) => state.accessManagement.currentUserPermissions.data);
  const isLoading = useSelector((state: RootState) => state.accessManagement.currentUserPermissions.isLoading);
  const error = useSelector((state: RootState) => state.accessManagement.currentUserPermissions.error);

  // Build confidential data map from minimal hierarchy (lightweight, fetched at app init)
  const rpnConfidentialMap = useSelector(selectRpnConfidentialMapFromMinimalHierarchy);
  const isConfidentialDataLoading = useSelector(selectProductHierarchyMinimalLoading);

  // Memoize user type checks (don't change unless userPermission changes)
  const userTypeChecks = useMemo(
    () => ({
      isTechAdmin: permissionsUtils.isTechAdmin(userPermission),
      isVerityAdmin: permissionsUtils.isVerityAdmin(userPermission),
      isReadOnly: permissionsUtils.isReadOnly(userPermission),
      hasExtendedAccessPL: permissionsUtils.hasExtendedAccessPL(userPermission),
    }),
    [userPermission]
  );

  // Memoize admin checks
  const adminChecks = useMemo(
    () => ({
      canManageAccessManagement: permissionsUtils.canManageAccessManagement(userPermission),
      canAccessAdminSection: permissionsUtils.canAccessAdminSection(userPermission),
      hasAnyPermissions: permissionsUtils.hasAnyPermissions(userPermission),
    }),
    [userPermission]
  );

  // Memoize permission check functions
  const permissionFunctions = useMemo(
    () => ({
      hasProductLineAccess: (productLineId: number) =>
        permissionsUtils.hasProductLineAccess(userPermission, productLineId),

      isProductLineAdmin: (productLineId: number) => permissionsUtils.isProductLineAdmin(userPermission, productLineId),

      hasConfidentialAccessForProductLine: (productLineId: number) =>
        permissionsUtils.hasConfidentialAccessForProductLine(userPermission, productLineId),

      canAccessConfidentialRPN: (productLineId: number, rpnId: number) => {
        const isConfidential = rpnConfidentialMap[rpnId] || false;
        return permissionsUtils.canAccessConfidentialRPN(userPermission, productLineId, isConfidential);
      },

      canEditProductLine: (productLineId: number) => permissionsUtils.canEditProductLine(userPermission, productLineId),

      canEditRPN: (productLineId: number, _rpnId: number) => {
        return permissionsUtils.canEditRPN(userPermission, productLineId);
      },

      getAccessibleProductLineIds: () => permissionsUtils.getAccessibleProductLineIds(userPermission),

      getAdminProductLineIds: () => permissionsUtils.getAdminProductLineIds(userPermission),

      getConfidentialAccessProductLineIds: () => permissionsUtils.getConfidentialAccessProductLineIds(userPermission),
    }),
    [userPermission, rpnConfidentialMap]
  );

  return {
    // User permission data
    userPermission,
    isLoading,
    error,

    // User type checks
    ...userTypeChecks,

    // Permission functions
    ...permissionFunctions,

    // Admin checks
    ...adminChecks,

    // Confidential data
    rpnConfidentialMap,
    isConfidentialDataLoading,
  };
};
